import { useStartQuiz } from '@repo/api/quiz/useStartQuiz';
import { ErrorCodes } from '@repo/lib/errorCodes';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { useBackTo } from '@/hooks/quiz';

import type { OngoingAttempt, StartQuizConflictResponse, StartQuizDialogProps } from '.';

const ONGOING_ATTEMPT_CODES: string[] = [ErrorCodes.FORBIDDEN.QUIZ.ATTEMPT_ONGOING, ErrorCodes.FORBIDDEN.QUIZ.ATTEMPT_ONGOING_OTHER_QUIZ];

export function useStartQuizDialog(startQuizDialogProps: StartQuizDialogProps) {
  const { state, onStateChange, backTo: backToProp } = startQuizDialogProps;
  const [_, quizId] = state;
  const navigate = useNavigate();
  const { search: attemptSuffix } = useBackTo(backToProp);
  const [ongoingAttempt, setOngoingAttempt] = useState<OngoingAttempt | null>(null);
  const [showForceOption, setShowForceOption] = useState<boolean>(false);
  const { mutateAsync, isError, isPending, errorMessage, reset } = useStartQuiz();

  const onOpenChange = (open: boolean) => {
    clearState();
    onStateChange([open, open ? quizId : null]);
  };

  const clearState = () => {
    reset();
    setShowForceOption(false);
    setOngoingAttempt(null);
  };

  const onSubmit = async (forceStart = false) => {
    if (!quizId) return;
    await mutateAsync({ quizId: quizId, force: forceStart })
      .then((attemptId) => {
        onOpenChange(false);
        navigate(`/quizzes/${quizId}/attempt/${attemptId}${attemptSuffix}`);
      })
      .catch((error) => {
        if (!isAxiosError<StartQuizConflictResponse>(error)) return;

        const conflict = error.response?.data;

        if (!conflict) return;

        if (conflict.code && ONGOING_ATTEMPT_CODES.includes(conflict.code)) {
          if (!forceStart) setShowForceOption(true);

          if (conflict.attemptId) {
            const ongoingQuizId = conflict.quizId ?? quizId;

            setOngoingAttempt({
              attemptId: conflict.attemptId,
              quizId: ongoingQuizId,
              quizName: conflict.quizName ?? null,
              isSameQuiz: ongoingQuizId === quizId,
            });
          }
        }
      });
  };

  const goToAttempt = () => {
    if (!ongoingAttempt) return;

    // `backTo` describes where the quiz being started should return to, so it
    // must not leak into an attempt of a different quiz
    const suffix = ongoingAttempt.isSameQuiz ? attemptSuffix : '';

    navigate(`/quizzes/${ongoingAttempt.quizId}/attempt/${ongoingAttempt.attemptId}${suffix}`);
  };

  return {
    state: {
      open: state[0],
      quizId: state[1],
      showForceOption,
      isError,
      isPending,
      errorMessage,
      ongoingAttempt,
    },
    actions: {
      onOpenChange,
      onSubmit,
      setShowForceOption,
      goToAttempt,
    },
  };
}
